import { notification } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  NEW_OPERATOR_SENTINEL,
  Operator,
  OperatorRow,
  toOperatorCreatePayload,
  toOperatorUpdatePayload,
} from "../../../types";
import {
  createOperator,
  disableOperator as disableOperatorApi,
  enableOperator as enableOperatorApi,
  listOperatorsIncludingDisabled,
  updateOperator,
} from "../../../../modules/integrations/accounts";
import { getErrorMessage } from "../../../utils/error";
import { ContentWrapper } from "../../common/styledComponents";
import PermissionsTable from "./PermissionsTable";
import { useAuth } from "@/modules/auth";

const TaxOpsView = () => {
  const { t } = useTranslation("Operators");
  const { getOperatorFirm } = useAuth();
  const [loading, setLoading] = useState(true);
  const [operators, setOperators] = useState<Array<Operator>>([]);

  /*
   * Disable and enable both answer with the changed operator, which replaces
   * its row. The table's status filter then decides whether the row shows.
   */
  const changeOperatorStatus = useCallback(
    async (
      row: OperatorRow,
      change: (id: string) => Promise<Operator>,
      successMessage: string
    ) => {
      try {
        setLoading(true);
        const changed = await change(row.id);
        setOperators((current) =>
          current.map((o) => (o.id === changed.id ? changed : o))
        );
        notification.success({ message: successMessage });
      } catch (err) {
        notification.error({ message: getErrorMessage(err) });
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const disableOperator = useCallback(
    (row: OperatorRow) =>
      changeOperatorStatus(
        row,
        disableOperatorApi,
        t("operators.disabledSuccess")
      ),
    [changeOperatorStatus, t]
  );

  const enableOperator = useCallback(
    (row: OperatorRow) =>
      changeOperatorStatus(
        row,
        enableOperatorApi,
        t("operators.enabledSuccess")
      ),
    [changeOperatorStatus, t]
  );

  /*
   * The table lists disabled operators too, behind its status filter, so an
   * admin can find and enable them again.
   */
  const fetchOperators = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listOperatorsIncludingDisabled();
      setOperators(data);
    } catch (err) {
      notification.error({ message: getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  const saveOperator = useCallback(
    async (draft: OperatorRow) => {
      try {
        setLoading(true);
        if (draft.id === NEW_OPERATOR_SENTINEL) {
          const created = await createOperator(toOperatorCreatePayload(draft));
          setOperators([
            created,
            ...operators.filter((o) => o.id !== draft.id),
          ]);
        } else {
          const updated = await updateOperator(
            draft.id,
            toOperatorUpdatePayload(draft)
          );
          setOperators(operators.map((o) => (o.id === draft.id ? updated : o)));
        }
      } catch (err) {
        notification.error({ message: getErrorMessage(err) });
        // Re-throw so the editable table keeps the row open for a retry
        // instead of committing an unsaved draft.
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [operators]
  );

  useEffect(() => {
    fetchOperators();
  }, [fetchOperators]);

  return (
    <ContentWrapper>
      <PermissionsTable
        defaultFirm={getOperatorFirm()}
        operators={operators}
        loading={loading}
        onDisable={disableOperator}
        onEnable={enableOperator}
        onSave={saveOperator}
      />
    </ContentWrapper>
  );
};

export default TaxOpsView;
